import { useEffect, useMemo, useState } from "react";

import { Anchor, Text, Button, Group, Modal, SegmentedControl, Select, Box } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import _ from "lodash";

import { descriptions } from "constants/levelData";
import { useSearchRouteInfo } from "hooks/routing";
import { useSearchAuthGate } from "hooks/useSearchAuthGate";
import { JobTitleOptionValue } from "lib/frontend/benchmarking/types";
import { useIsSmallerThan } from "ui/theme/helpers";
import { levelLabel, sortLevels } from "utils/utils";

import { FamilyLevelAssistedPicker } from "./FamilyLevelAssistedPicker";
import { PageContentBoxWithInternalPadding } from "./layouts/PageContentBox";
import { RegistrationModal } from "./RegistrationModal";
import { FilterDrawerButton } from "./TitlePage/FilterDrawerButton";

export function SearchByBar(): JSX.Element {
  const isSmall = useIsSmallerThan("lg");
  const { query, focusedJobTitle, searchMode, replaceQuery } = useSearchRouteInfo();
  const [searchBy, setSearchBy] = useState<"Family & Level" | "title">(
    searchMode === "title" ? "title" : "Family & Level",
  );

  const [selectedTitle, setSelectedTitle] = useState(focusedJobTitle);

  const [familyMap, setFamilyMap] = useState<Record<string, string[]>>({});
  const [selectedFamily, setSelectedFamily] = useState<string | null>(query.jobFamily?.[0] ?? null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(query.level?.[0] ?? null);

  const [opened, { toggle }] = useDisclosure(false);

  const searchAuthGateControl = useSearchAuthGate();

  useEffect(() => {
    setSelectedLevel(query.level?.[0] ?? null);
  }, [query.level]);

  const [jobTitles, setJobTitles] = useState<JobTitleOptionValue[]>(
    _.map(query.title, (t) => ({ normalizedTitle: t, jobFunction: "", count: 0 })),
  );

  const titleLabels = useMemo(() => {
    return _.map(jobTitles, (jt) => ({
      value: jt.normalizedTitle,
      label: jt.normalizedTitle,
      // group: jt.jobFunction || "Other",
    }));
  }, [jobTitles]);

  useEffect(() => {
    setSelectedTitle("");
  }, [focusedJobTitle]);

  function addToJobTitles(newTitles: JobTitleOptionValue[]) {
    setJobTitles((oldTitles) => {
      const resultingTitles = _([...oldTitles, ...newTitles])
        // order before uniq to keep the new counts from the server
        .orderBy("count", "desc")
        .uniqBy("normalizedTitle")
        .valueOf();

      return resultingTitles;
    });
  }

  async function fetchJobTitles(page = 0, query = "") {
    return fetch(`/api/benchmarking/titles-autocomplete/${query}?page=${page}`)
      .then((res) => res.json())
      .then((data) => {
        const formattedJobTitles = _.map(data, (jt) => ({
          normalizedTitle: jt.normalizedTitle,
          jobFunction: jt.jobFunction,
          count: jt.count,
        }));

        addToJobTitles(formattedJobTitles);
      });
  }

  async function fetchJobFamilyMap() {
    return fetch("/api/benchmarking/families/all")
      .then((res) => res.json())
      .then((data) => {
        delete data["Other"];
        delete data["other"];
        setFamilyMap(data);
      });
  }

  useEffect(() => {
    fetchJobTitles(0, "_");
    fetchJobFamilyMap();
  }, []);

  function canExecuteSearch(): boolean | undefined {
    if (searchBy === "title") {
      if (selectedTitle === query.title?.[0]) {
        return true;
      }
    } else if (searchBy === "Family & Level") {
      if (selectedFamily === query.jobFamily?.[0] && selectedLevel === query.level?.[0]) {
        return true;
      }
    }

    return searchBy === "title" ? !selectedTitle : !selectedFamily || !selectedLevel;
  }

  function submit() {
    if (!searchAuthGateControl.handleSearchAttempt()) {
      return;
    }

    if (searchBy === "Family & Level") {
      replaceQuery({
        jobFamily: [selectedFamily ?? ""],
        level: _.compact([selectedLevel]),
        title: [],
        yoe: [],
      });
    } else if (searchBy === "title") {
      replaceQuery({
        title: [selectedTitle ?? ""],
        jobFamily: [],
        level: [],
      });
    }
  }

  function renderSearchByTitle() {
    return (
      <Select
        leftSection={<IconSearch size={16} />}
        size="sm"
        value={selectedTitle}
        searchable
        data={titleLabels}
        limit={10}
        placeholder="Job Title"
        clearable
        onSearchChange={(query) => {
          fetchJobTitles(0, query);
        }}
        onChange={(v) => {
          // @ts-expect-error TS2345
          setSelectedTitle(v);
        }}
        // onKeyDown={(e) => {
        //   if (e.nativeEvent.code === "Enter") {
        //     submit();
        //   }
        // }}
      />
    );
  }

  function renderSearchByFamilyAndLevel() {
    const families = _.keys(familyMap);

    const levelsOptions = sortLevels(familyMap[selectedFamily ?? ""] ?? [])
      .filter((level) => {
        return !_.isNil(descriptions[level as keyof typeof descriptions]);
      })
      .map((level) => {
        return {
          value: level,
          label: levelLabel(level),
        };
      });

    return (
      <>
        <Select
          size="sm"
          searchable
          placeholder="Family"
          clearable
          data={_.map(families, (family) => {
            return {
              value: family,
              label: family,
            };
          })}
          value={selectedFamily}
          nothingFoundMessage="No families found"
          onChange={(v) => {
            setSelectedFamily(v);
            // setSelectedLevel(null);
          }}
        />
        <Select
          w={{ base: "auto", lg: 120, xl: 120 }}
          size="sm"
          searchable
          placeholder="Level"
          clearable
          data={levelsOptions}
          value={selectedLevel}
          nothingFoundMessage={
            selectedFamily ? "No levels found for this family" : "Select a family first"
          }
          allowDeselect
          onChange={(v) => {
            setSelectedLevel(v);
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.code === "Enter") {
              submit();
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <RegistrationModal
        opened={searchAuthGateControl.regModalOpened}
        toggle={searchAuthGateControl.toggleRegModal}
      />
      <PageContentBoxWithInternalPadding overrideMaxWidth={1200} py={{ base: 4, lg: "inherit" }}>
        <Box
          p={8}
          h={{ sm: "auto", lg: 60 }}
          w="100%"
          sx={{
            display: "flex",
            gap: 8,
            ...(isSmall
              ? {
                  flexDirection: "column",
                }
              : {
                  flexDirection: "row",
                  alignItems: "center",
                }),
          }}
        >
          <Group wrap="nowrap">
            <Text
              fz="xs"
              ta="center"
              sx={{
                // hack to alter this one item's flex gap
                ...(isSmall
                  ? {
                      lineHeight: "0.7",
                      overflow: "visible",
                    }
                  : {}),
              }}
            >
              Search By:
            </Text>
            <SegmentedControl
              size="xs"
              transitionDuration={125}
              transitionTimingFunction="linear"
              fullWidth={isSmall}
              // w={{ base: "100%", lg: "auto" }}
              // sx={{flex:}}
              value={searchBy}
              // @ts-expect-error TS2345
              onChange={(value: typeof searchBy) => {
                if (value) {
                  setSearchBy(value);
                }
              }}
              data={[
                {
                  value: "Family & Level",
                  label: "Family & Level",
                },
                {
                  value: "title",
                  label: "Job Title",
                },
              ]}
              styles={(theme) => ({
                active: {
                  backgroundColor: theme.colors.brand[0],
                  color: theme.colors.brand[6],
                  border: `1px solid ${theme.colors.gray[3]}`,
                },
                root: {
                  border: `1px solid ${theme.colors.gray[3]}`,
                  backgroundColor: "#FAFAFC",
                },
                label: {
                  color: theme.colors.gray[5],
                },
              })}
            />
          </Group>
          <Group grow w={{ base: "100%", lg: 440 }} wrap="nowrap">
            {searchBy === "title" && renderSearchByTitle()}
            {searchBy === "Family & Level" && renderSearchByFamilyAndLevel()}
          </Group>
          <Button
            size="sm"
            leftSection={<IconSearch size={16} />}
            onClick={submit}
            disabled={canExecuteSearch()}
            data-heap-id="benchmarking-search-button"
            variant="primary"
          >
            Search
          </Button>
          {searchBy === "Family & Level" && (
            <Anchor
              maw={{ lg: 150 }}
              ta={{ base: "center", lg: "left" }}
              lineClamp={2}
              onClick={toggle}
              ml={4}
              size="xs"
            >
              Need help choosing Family or Level?
            </Anchor>
          )}
        </Box>
      </PageContentBoxWithInternalPadding>
      <Group justify="center" w="100%" display={{ base: "flex", lg: "none" }}>
        <FilterDrawerButton w="100%" />
      </Group>
      <Modal
        centered
        opened={opened}
        onClose={toggle}
        size={800}
        title="Don't know what job family or level to pick?"
        styles={{
          body: {
            padding: "16px",
          },
          inner: {
            padding: "16px",
          },
          title: {
            fontWeight: "bold",
            color: "black",
            marginLeft: "16px",
          },
          close: {
            marginRight: "16px",
          },
          header: {
            padding: 0,
            paddingTop: 16,
            paddingBottom: 16,
          },
        }}
      >
        <FamilyLevelAssistedPicker
          close={toggle}
          onSelect={(args) => {
            toggle();

            setSelectedFamily(args.family);
            setSelectedLevel(args.level);

            if (!searchAuthGateControl.handleSearchAttempt()) {
              return;
            }

            replaceQuery({
              jobFamily: [args.family],
              level: [args.level],
              title: [],
            });
          }}
        />
      </Modal>
    </>
  );
}
